import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useLoader } from "@/hooks/LoaderProvider";
import { ArrowRightOutlined } from "@/config/antd";
import {
  setCurrentStep,
  updateRoleFormData,
  setUserIDs,
  fetchSingleRole,
  setRoleFormForEdit,
  selectSingleRole,
} from "@/store/redux/slices/roles/role_slice";
import Button from "@/components/common/Button";
import Back from "@/components/specific/Back";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import Success from "@/components/common/alerts/Success";
import Delete from "@/components/common/alerts/Delete";
import MemberPicker from "@/components/specific/Member_Picker";
import InviteMembersDialog from "@/ui/Invite_Members";
import DOMPurify from "dompurify";

const roleEditFetchById = new Map();

const mapRoleUsersToMembers = (users, roleName) => {
  if (!users?.length) return [];

  return users
    .flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
    .map((user) => {
      if (typeof user === "string") return null;
      if (!user?._id && !user?.id) return null;
      return {
        id: user._id || user.id,
        name: user.fullname || user.username,
        profileImage: user.profileImage,
        role: roleName,
      };
    })
    .filter(Boolean);
};

const getRoleForEdit = (dispatch, id) => {
  if (!roleEditFetchById.has(id)) {
    const promise = dispatch(fetchSingleRole({ id }))
      .unwrap()
      .finally(() => roleEditFetchById.delete(id));
    roleEditFetchById.set(id, promise);
  }
  return roleEditFetchById.get(id);
};

const AddRolesStep1 = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const singleRole = useSelector(selectSingleRole);
  const appliedRoleIdRef = useRef(null);

  const [formData, setFormData] = useState({
    roleName: "",
    roleDescription: "",
    selectedMembers: [],
    isInviteMembersModalVisible: false,
    isDeleteModalVisible: false,
    isSuccessModalVisible: false,
    memberToDelete: null,
  });

  useEffect(() => {
    if (!id) return;
    if (appliedRoleIdRef.current === id) return;

    let cancelled = false;

    const applyRoleToForm = (roleData) => {
      dispatch(
        setRoleFormForEdit({
          roleId: roleData?._id,
          roleName: roleData?.name,
          description: roleData?.description,
          permissions: roleData?.permissions || [],
          users: roleData?.users || [],
          organizationID: roleData?.organizationID,
        })
      );

      setFormData((prevData) => ({
        ...prevData,
        roleName: roleData?.name || "",
        roleDescription: roleData?.description || "",
        selectedMembers: mapRoleUsersToMembers(roleData?.users, roleData?.name),
      }));
      appliedRoleIdRef.current = id;
    };

    const loadRoleForEdit = async () => {
      if (singleRole?._id === id && Array.isArray(singleRole?.users)) {
        applyRoleToForm(singleRole);
        return;
      }

      showLoader();
      try {
        const roleData = await getRoleForEdit(dispatch, id);
        if (cancelled) return;
        applyRoleToForm(roleData);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load role for edit mode:", error);
        }
      } finally {
        if (!cancelled) {
          hideLoader();
        }
      }
    };

    loadRoleForEdit();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, hideLoader, id, showLoader]);

  const handleMembersSelected = (newMembers) => {
    setFormData((prevData) => ({
      ...prevData,
      selectedMembers: [...prevData.selectedMembers, ...newMembers],
    }));
  };

  const handleDeleteMember = (member) => {
    setFormData((prevData) => ({
      ...prevData,
      memberToDelete: member,
      isDeleteModalVisible: true,
    }));
  };

  const handleDeleteConfirm = () => {
    setFormData((prevData) => ({
      ...prevData,
      isDeleteModalVisible: false,
    }));
    showLoader();

    setTimeout(() => {
      hideLoader();
      setFormData((prevData) => ({
        ...prevData,
        selectedMembers: prevData.selectedMembers.filter(
          (m) => m.id !== formData.memberToDelete.id
        ),
      }));
      setFormData((prevData) => ({
        ...prevData,
        isSuccessModalVisible: true,
      }));
    }, 2000);
  };

  const handleUpdateMember = (memberId, newRole) => {
    setFormData((prevData) => ({
      ...prevData,
      selectedMembers: prevData.selectedMembers.map((member) =>
        member.id === memberId ? { ...member, role: newRole } : member
      ),
    }));
  };

  // Roles
  const roleOptions = [
    { value: "Super Admin", label: "Super Admin" },
    { value: "Campaign Manager", label: "Campaign Manager" },
    { value: "Event Admin", label: "Event Admin" },
    { value: "Finance Admin", label: "Finance Admin" },
    { value: "Communication Manager", label: "Communication Manager" },
    { value: "Canvasser", label: "Canvasser" },
    { value: "Volunteer", label: "Volunteer" },
  ];

  const handleProceed = () => {
    showLoader();
    try {
      // Sanitize the inputs
      const sanitizedRoleName = DOMPurify.sanitize(formData.roleName.trim(), {
        ALLOWED_TAGS: [], // Remove all HTML tags
      });

      const sanitizedDescription = DOMPurify.sanitize(
        formData.roleDescription.trim(),
        {
          ALLOWED_TAGS: [], // Remove all HTML tags
        }
      );

      dispatch(
        updateRoleFormData({
          roleName: sanitizedRoleName,
          description: sanitizedDescription,
        })
      );

      // Save selected users
      const userIDs = formData.selectedMembers.map((member) => member.id);
      dispatch(setUserIDs(userIDs));

      dispatch(setCurrentStep(2));

      setTimeout(() => {
        hideLoader();
        navigate(
          isEditMode
            ? `/dashboard/roles/edit/${id}/step-2`
            : "/dashboard/roles/add-roles-step-2"
        );
      }, 2000);
    } catch (error) {
      console.error("Error in step 1:", error);
      hideLoader();
    }
  };

  const isFormFilled =
    formData.roleName.trim() !== "" && formData.roleDescription.trim() !== "";

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <Back />
      </div>

      <div className="flex-row-reverse gap-40 md:flex">
        <div className="w-full">
          <h1 className="mb-2 text-left heading-medium sm:text-3xl">
            {isEditMode ? "Edit Role" : "Add New Role"}
          </h1>
          <p className="mb-6 text-left text-small">
            Describe all the parameters to create a new role
          </p>
          <h1 className="mb-4 font-semibold text-left text-bold sm:text-2xl">
            Describe what this role is all about
          </h1>
          <div className="space-y-6">
            <Input
              label="Role Title"
              placeholder="Enter role name"
              type="select"
              options={roleOptions}
              value={formData.roleName}
              onChange={(e) =>
                setFormData((prevData) => ({
                  ...prevData,
                  roleName: e.target.value,
                }))
              }
            />
            <TextEditor
              label="Role Description"
              placeholder="Enter role description"
              value={formData.roleDescription}
              onChange={(value) =>
                setFormData((prevData) => ({
                  ...prevData,
                  roleDescription: value,
                }))
              }
            />

            <div className="space-y-2">
              <h1 className="text-left text-bold">Add Users to Role</h1>
              <p className="text-left text-gray-500 text-small">
                Select users that&apos;ll have this role and permissions
              </p>
              <div>
                <MemberPicker
                  members={formData.selectedMembers}
                  roles={roleOptions}
                  onOpenModal={() =>
                    setFormData((prevData) => ({
                      ...prevData,
                      isInviteMembersModalVisible: true,
                    }))
                  }
                  onDeleteMember={handleDeleteMember}
                  onUpdateMember={handleUpdateMember}
                />
              </div>
            </div>

            <Button
              text={isEditMode ? "Update Privileges" : "Proceed"}
              icon={<ArrowRightOutlined />}
              onClick={handleProceed}
              isActive={isFormFilled}
              color="darkBlue"
              className={`w-full mt-6 ${
                isFormFilled
                  ? "bg-primary text-white"
                  : "bg-darkBlue text-primary"
              }`}
            />
          </div>
        </div>
      </div>

      {/* Invite Members Dialog */}

      <InviteMembersDialog
        isVisible={formData.isInviteMembersModalVisible}
        onClose={() =>
          setFormData((prevData) => ({
            ...prevData,
            isInviteMembersModalVisible: false,
          }))
        }
        onMembersSelected={handleMembersSelected}
      />

      <Delete
        isVisible={formData.isDeleteModalVisible}
        onClose={() =>
          setFormData((prevData) => ({
            ...prevData,
            isDeleteModalVisible: false,
          }))
        }
        onProceed={handleDeleteConfirm}
        header={`Remove ${formData.memberToDelete?.name}`}
        paragraph={`Are you sure you want to remove <span class="text-primary">${formData.memberToDelete?.name}</span> from this event?`}
        proceedButtonText="Remove"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={formData.isSuccessModalVisible}
        onClose={() =>
          setFormData((prevData) => ({
            ...prevData,
            isSuccessModalVisible: false,
          }))
        }
        header="Member Removed"
        paragraph={`${formData.memberToDelete?.name} has been removed from this event.`}
        buttonText="Close"
        onButtonClick={() =>
          setFormData((prevData) => ({
            ...prevData,
            isSuccessModalVisible: false,
          }))
        }
      />
    </div>
  );
};

export default AddRolesStep1;
